import { createApp } from "petite-vue";
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import lightGallery from 'lightgallery';
import lgThumbnail from 'lightgallery/plugins/thumbnail';

import { initMap } from "./map";
import initArchiveOffer from "./archive-offer";
import initArchiveLoader from "./archive-loader";

import.meta.glob(['@images/**']);
import.meta.env.DEV && import("@styles/main.sass");

interface Header {
    opened: boolean

    handleOpenedHeader(state: boolean): void
}

const headerEl = document.querySelector('[data-header]'),
    headerScope: Header = {
        opened: false,
        handleOpenedHeader(state) {
            document.body.classList.toggle("u-hiddenOverflow", state)
        }
    }

headerEl && createApp(headerScope).mount(headerEl);
initArchiveOffer();

// --------------------------------- Sticky header on scroll
(() => {
    const header = document.querySelector<HTMLElement>('[data-header]');
    if (!header) return;

    const logoImg = header.querySelector<HTMLImageElement>('[data-logo-dark]');
    const logoDark = logoImg?.dataset.logoDark || '';
    const logoDefault = logoImg?.dataset.logoDefault || '';

    const updateSticky = () => {
        // Skip sticky logic when mobile menu is open
        if (header.classList.contains('is-opened')) return;

        const scroll = window.scrollY;
        const currentIsSticky = header.classList.contains('is-sticky');

        // Hysteresis: turn on at 100, turn off at 50 to prevent flickering
        let shouldBeSticky = currentIsSticky;
        if (scroll > 100) {
            shouldBeSticky = true;
        } else if (scroll < 50) {
            shouldBeSticky = false;
        }

        if (shouldBeSticky !== currentIsSticky) {
            header.classList.toggle('is-sticky', shouldBeSticky);

            // Toggle logos based on sticky state
            if (logoImg) {
                const targetLogo = shouldBeSticky ? logoDark : logoDefault;
                if (targetLogo && logoImg.src !== targetLogo) {
                    logoImg.src = targetLogo;
                }
            }
        }
    };

    updateSticky();

    let ticking = false;
    window.addEventListener('scroll', () => {
        if (!ticking) {
            window.requestAnimationFrame(() => {
                updateSticky();
                ticking = false;
            });
            ticking = true;
        }
    }, { passive: true });
})();

//----------------------------------------

document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll<HTMLElement>(".Map").forEach((el) => initMap(el))
    initArchiveLoader();
});

// --------------------------- Count-up (HomeAbout + HomeHero)
(() => {
    const DURATION = 2400; // ms
    const easeOutQuad = (t: number) => t * (2 - t);

    function findTargets(root: ParentNode | Document = document): HTMLElement[] {
        const list = Array.from(root.querySelectorAll<HTMLElement>('[data-count]'));

        if (!(window as any).__countDebugOnce__) {
            (window as any).__countDebugOnce__ = true;
            console.log('[CountUp] found:', list.length, list);
        }

        return list;
    }

    function animate(el: HTMLElement) {
        if (el.classList.contains('is-counted')) return;

        const raw = (el.getAttribute('data-count') || '').trim();
        // Allow float
        const target = parseFloat(raw.replace(',', '.'));
        if (!Number.isFinite(target)) {
            console.warn('[CountUp] invalid data-count:', raw, el);
            return;
        }

        const prefix = el.getAttribute('data-prefix') ?? '';
        const suffix = el.getAttribute('data-suffix') ?? '';

        el.classList.add('is-counted');

        // bezpečně sestavit DOM (a hlavně neshodit úmyslnou mezeru v sufixu)
        el.textContent = '';

        if (prefix) {
            const p = document.createElement('span');
            p.className = 'u-prefix';
            p.textContent = prefix;
            el.appendChild(p);
        }

        const v = document.createElement('span');
        v.className = 'u-val';
        v.textContent = '0';
        el.appendChild(v);

        if (suffix) {
            const s = document.createElement('span');
            s.className = 'u-suffix';
            s.textContent = suffix;
            el.appendChild(s);
        }

        const span = v;


        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

        // Determine decimal places from target or raw string
        const hasDecimals = raw.includes('.') || raw.includes(',');
        const isRating = el.closest('.HomeHero-stat--rating');
        const decimals = (hasDecimals || isRating) ? 1 : 0;

        const format = (n: number) => n.toLocaleString('cs-CZ', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).replace(',', '.');


        if (reduceMotion) {
            span.textContent = format(target);
            return;
        }

        const start = performance.now();
        const step = (now: number) => {
            const t = Math.min(1, (now - start) / DURATION);
            const val = target * easeOutQuad(t); // No rounding here yet
            span.textContent = format(val);
            if (t < 1) requestAnimationFrame(step);
            else span.textContent = format(target);
        };
        requestAnimationFrame(step);
    }


    function inViewport(el: Element) {
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const vw = window.innerWidth || document.documentElement.clientWidth;
        return r.bottom > 0 && r.right > 0 && r.top < vh && r.left < vw;
    }

    function run() {
        const targets = findTargets();
        if (!targets.length) return;
        targets.forEach((el) => {
            if (inViewport(el)) animate(el);
        });
    }

    const start = () => {
        run();
        setTimeout(run, 0);
        setTimeout(run, 150);
    };

    if (document.readyState !== 'loading') start();
    else document.addEventListener('DOMContentLoaded', start, { once: true });


    window.addEventListener('scroll', run, { passive: true });
    window.addEventListener('resize', run);
})();


// --------------------------------- Lightbox galleries (lightGallery + single images)
(() => {
    // 1. Standard Gutenberg Galleries
    const galleries = document.querySelectorAll<HTMLElement>('.wp-block-gallery');
    galleries.forEach(gallery => {
        lightGallery(gallery, {
            plugins: [lgThumbnail],
            selector: '.wp-block-image > a',
            thumbnail: true,
            speed: 300,
        });
    });

    // 2. Single images or images in columns linking to media files
    // Select all links that point to an image file and are NOT inside an already initialized gallery
    const singleImageLinks = document.querySelectorAll<HTMLElement>(
        'a[href$=".jpg"]:not(.wp-block-gallery a), a[href$=".jpeg"]:not(.wp-block-gallery a), a[href$=".png"]:not(.wp-block-gallery a), a[href$=".webp"]:not(.wp-block-gallery a)'
    );

    if (singleImageLinks.length) {
        // Group them into one big gallery or keep separate? 
        // Best to group all single images on the page into one gallery for easy arrows (prepinani)
        const wrapper = document.body;
        lightGallery(wrapper, {
            plugins: [lgThumbnail],
            selector: 'a[href$=".jpg"]:not(.wp-block-gallery a), a[href$=".jpeg"]:not(.wp-block-gallery a), a[href$=".png"]:not(.wp-block-gallery a), a[href$=".webp"]:not(.wp-block-gallery a)',
            thumbnail: true,
            speed: 300,
        });
    }
})();

(() => {
    // Property gallery — video (iframe) + photos in one lightGallery
    const videoTrigger = document.querySelector<HTMLElement>('[data-role="video-trigger"]');
    const links = Array.from(document.querySelectorAll<HTMLAnchorElement>(
        '.PropertyTop-grid > a, .PropertyBottomGallery-grid > a, .PropertyDetails-floorplan'
    ));
    if (!links.length && !videoTrigger) return;

    const dynamicEl: Array<{ src: string; thumb: string; iframe?: boolean }> = [];
    let videoOffset = 0;

    // Video as first slide (lightweight iframe, no lgVideo)
    if (videoTrigger) {
        const vSrc = videoTrigger.dataset.src || '';
        const vType = videoTrigger.dataset.type || '';

        let iframeSrc = vSrc;
        if (vType === 'youtube') {
            const sep = vSrc.includes('?') ? '&' : '?';
            iframeSrc = `${vSrc}${sep}autoplay=1&rel=0`;
        }

        // Extract a thumb for the video
        let vThumb = '';
        if (vType === 'youtube') {
            const ytId = vSrc.split('/').pop()?.split('?')[0] || '';
            vThumb = `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
        }

        dynamicEl.push({ src: iframeSrc, thumb: vThumb, iframe: true });
        videoOffset = 1;
    }

    // Photo slides
    links.forEach(link => {
        const img = link.querySelector('img');
        dynamicEl.push({
            src: link.href,
            thumb: img?.src || link.href,
        });
    });

    const wrapper = document.createElement('div');
    wrapper.style.display = 'none';
    document.body.appendChild(wrapper);

    const gallery = lightGallery(wrapper, {
        plugins: [lgThumbnail],
        thumbnail: true,
        speed: 300,
        dynamic: true,
        dynamicEl,
        closeOnTap: true,
        showCloseIcon: true,
    });

    // Video trigger opens lightGallery at index 0 (bypasses old modal)
    if (videoTrigger) {
        videoTrigger.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopImmediatePropagation();
            gallery.openGallery(0);
        }, true); // capture phase — runs before old modal handler
    }

    // Photo links open at their offset index
    links.forEach((link, i) => {
        link.addEventListener('click', (e) => {
            e.preventDefault();
            gallery.openGallery(i + videoOffset);
        });
    });
})();


//--------------------------------------------Investiční kalkulačka


(() => {
    const priceRange = document.querySelector('[data-role="price-range"]') as HTMLInputElement
    const loanRange = document.querySelector('[data-role="loan-range"]') as HTMLInputElement
    const maturityRange = document.querySelector('[data-role="maturity-range"]') as HTMLInputElement

    const priceInput = document.querySelector('[data-role="price-input"]') as HTMLInputElement
    const loanInput = document.querySelector('[data-role="loan-input"]') as HTMLInputElement
    const maturityInput = document.querySelector('[data-role="maturity-input"]') as HTMLInputElement

    const paymentDisplay = document.querySelector('[data-role="payment-display"]')

    // Hidden inputs for form (keeping names generic or as is to avoid breaking form handler if strictly coupled)
    const hiddenPrice = document.querySelector('[data-role="price-hidden"]') as HTMLInputElement
    const hiddenLoan = document.querySelector('[data-role="rent-hidden"]') as HTMLInputElement // reusing rent-hidden for loan
    const hiddenMaturity = document.querySelector('[data-role="annual-rent-hidden"]') as HTMLInputElement // reusing annual-rent for maturity
    const hiddenPayment = document.querySelector('[data-role="yield-hidden"]') as HTMLInputElement // reusing yield for payment

    const resultPanel = document.querySelector('[data-role="result-panel"]') as HTMLElement
    const sendButton = document.querySelector('[data-role="send-button"]') as HTMLButtonElement

    const modalOverlay = document.querySelector('[data-role="ic-modal"]') as HTMLElement
    const modalCloseBtns = document.querySelectorAll('[data-role="ic-modal-close"]')

    if (
        !priceRange ||
        !loanRange ||
        !maturityRange ||
        !priceInput ||
        !loanInput ||
        !maturityInput ||
        !resultPanel ||
        !sendButton ||
        !modalOverlay
    ) {
        return
    }

    const RATE = 4.59;

    const parseNumber = (value) => {
        return Number(String(value).replace(/\s+/g, '').replace(/Kč/g, '').replace(/let/g, '').replace(/\./g, '').replace(/,/g, '.')) || 0
    }

    const formatCurrency = (value) => {
        return value.toLocaleString('cs-CZ', { maximumFractionDigits: 0 }) + ' Kč'
    }

    const formatYears = (value) => {
        return value.toLocaleString('cs-CZ', { maximumFractionDigits: 0 }) + ' let'
    }

    const updateRangeTrack = (range) => {
        if (!range) return

        const min = Number(range.min) || 0
        const max = Number(range.max) || 100
        const value = Number(range.value)
        const percent = ((value - min) / (max - min)) * 100

        range.style.background =
            `linear-gradient(to right,
        var(--colorPrimary) 0%,
        var(--colorPrimary) ${percent}%,
        var(--colorSecondary) ${percent}%,
        var(--colorSecondary) 100%)`
    }

    const recalc = () => {
        const price = Number(priceRange.value)
        const loan = Number(loanRange.value)
        const years = Number(maturityRange.value)

        let monthlyPayment = 0;

        if (loan > 0 && years > 0) {
            const i = (RATE / 100) / 12;
            const n = years * 12;

            if (i === 0) {
                monthlyPayment = loan / n;
            } else {
                monthlyPayment = loan * (i * Math.pow(1 + i, n)) / (Math.pow(1 + i, n) - 1);
            }
        }

        if (paymentDisplay) paymentDisplay.textContent = formatCurrency(Math.round(monthlyPayment))

        if (hiddenPrice) hiddenPrice.value = String(price)
        if (hiddenLoan) hiddenLoan.value = String(loan)
        if (hiddenMaturity) hiddenMaturity.value = String(years)
        if (hiddenPayment) hiddenPayment.value = Math.round(monthlyPayment).toString()

        resultPanel.classList.add('is-calculated')
        sendButton.disabled = false
    }

    const syncInputsFromRanges = () => {
        const price = Number(priceRange.value)
        const loan = Number(loanRange.value)
        const years = Number(maturityRange.value)

        priceInput.value = formatCurrency(price)
        loanInput.value = formatCurrency(loan)
        maturityInput.value = formatYears(years)

        updateRangeTrack(priceRange)
        updateRangeTrack(loanRange)
        updateRangeTrack(maturityRange)

        recalc()
    }

    loanRange.value = String(Number(priceRange.value) * 0.8)
    syncInputsFromRanges()

    priceRange.addEventListener('input', () => {
        const price = Number(priceRange.value)
        loanRange.value = String(price * 0.8)
        syncInputsFromRanges()
    })
    loanRange.addEventListener('input', syncInputsFromRanges)
    maturityRange.addEventListener('input', syncInputsFromRanges)

    const handleInputBlur = (input: HTMLInputElement, range: HTMLInputElement, isYear = false) => {
        const raw = parseNumber(input.value)
        const min = Number(range.min)
        const max = Number(range.max)
        const value = Math.min(Math.max(raw || min, min), max)

        range.value = String(value)

        if (input === priceInput) {
            loanRange.value = String(value * 0.8)
        }

        syncInputsFromRanges()
    }

    priceInput.addEventListener('blur', () => handleInputBlur(priceInput, priceRange))
    loanInput.addEventListener('blur', () => handleInputBlur(loanInput, loanRange))
    maturityInput.addEventListener('blur', () => handleInputBlur(maturityInput, maturityRange, true))

    const openModal = () => {
        recalc()
        modalOverlay.classList.add('is-opened')
        modalOverlay.setAttribute('aria-hidden', 'false')
        document.body.classList.add('has-ic-modal-open')
    }

    const closeModal = () => {
        modalOverlay.classList.remove('is-opened')
        modalOverlay.setAttribute('aria-hidden', 'true')
        document.body.classList.remove('has-ic-modal-open')
    }

    sendButton.addEventListener('click', () => {
        if (sendButton.disabled) return
        openModal()
    })

    modalCloseBtns.forEach((btn) => {
        btn.addEventListener('click', closeModal)
    })

    modalOverlay.addEventListener('click', (e) => {
        if (e.target === modalOverlay) {
            closeModal()
        }
    })

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modalOverlay.classList.contains('is-opened')) {
            closeModal()
        }
    })
})();

//-------------------ouško

(() => {

    const middle = document.querySelector<HTMLElement>(".Property-middle")
    const card = document.querySelector<HTMLElement>('[data-role="broker-card"]')

    if (!middle || !card) return


    const io = new IntersectionObserver(
        ([entry]) => {
            if (entry.isIntersecting) card.classList.remove("is-ear")
            else card.classList.add("is-ear")
        },
        {
            threshold: 0.05,
            rootMargin: "-20px 0px -20px 0px",
        }
    )

    io.observe(middle)
})();

//------------------
document.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-open-lightbox]")
    if (!btn) return

    const group = btn.dataset.openLightbox
    if (!group) return

    const first = document.querySelector<HTMLElement>(`[data-lightbox="${CSS.escape(group)}"]`)
    first?.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }))
});

//----------------------------- přepínání recenzí
(() => {
    const root = document.querySelector<HTMLElement>("[data-rating-tabs]")
    if (!root) return

    const tabs = Array.from(root.querySelectorAll<HTMLButtonElement>("[data-rating-tab]"))
    const panels = Array.from(root.querySelectorAll<HTMLElement>("[data-rating-panel]"))

    const setActive = (key: string) => {
        tabs.forEach((btn) => {
            const active = btn.dataset.ratingTab === key
            btn.classList.toggle("is-active", active)
            btn.setAttribute("aria-selected", active ? "true" : "false")
        })

        panels.forEach((p) => {
            const active = p.dataset.ratingPanel === key
            p.classList.toggle("is-active", active)
            p.toggleAttribute("hidden", !active)
        })
    }

    tabs.forEach((btn) => btn.addEventListener("click", () => setActive(btn.dataset.ratingTab || "firmy")))

    // default
    setActive("firmy")
})()

    //----------------------------- PropertyTop Video Modal
    ; (() => {
        const trigger = document.querySelector<HTMLElement>('[data-role="video-trigger"]')
        const modal = document.querySelector<HTMLElement>('[data-role="video-modal"]')
        if (!trigger || !modal) return

        const body = modal.querySelector<HTMLElement>('[data-role="video-modal-body"]')
        const closeBtn = modal.querySelector<HTMLElement>('[data-role="video-modal-close"]')
        if (!body || !closeBtn) return

        const type = trigger.dataset.type
        const src = trigger.dataset.src

        const closeModal = () => {
            modal.classList.remove('is-opened')
            modal.setAttribute('aria-hidden', 'true')
            document.body.classList.remove('u-hiddenOverflow')
            body.innerHTML = '' // Stop video
        }

        const openModal = () => {
            let html = ''

            if (type === 'youtube') {
                // Ensure autoplay and unmuted
                // Classic embed: https://www.youtube.com/embed/VIDEO_ID?params...
                // We append params. Assuming src includes parameters or not, we might need to be careful.
                // But usually raw embed link is just the base.
                // If src already has '?', assume it has some params.
                const separator = src?.includes('?') ? '&' : '?'
                const url = `${src}${separator}autoplay=1&mute=0&rel=0`

                html = `
                <iframe
                    class="PropertyTop-modalVideo"
                    src="${url}"
                    title="Video"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                ></iframe>
            `
            } else if (type === 'file') {
                html = `
                <video class="PropertyTop-modalVideo" autoplay controls playsinline>
                    <source src="${src}">
                </video>
            `
            } else if (type === 'iframe') {
                const separator = src?.includes('?') ? '&' : '?'
                const url = `${src}${separator}autoplay=1&muted=0&background=0`

                html = `
                <iframe
                    class="PropertyTop-modalVideo"
                    src="${url}"
                    title="Video"
                    frameborder="0"
                    allow="autoplay; fullscreen"
                    allowfullscreen
                ></iframe>
            `
            }

            body.innerHTML = html
            modal.classList.add('is-opened')
            modal.setAttribute('aria-hidden', 'false')
            document.body.classList.add('u-hiddenOverflow')
        }

        trigger.addEventListener('click', (e) => {
            e.preventDefault()
            openModal()
        })

        closeBtn.addEventListener('click', closeModal)

        modal.addEventListener('click', (e) => {
            if (e.target === modal) closeModal()
        })

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && modal.classList.contains('is-opened')) {
                closeModal()
            }
        })
    })()

interface Broker {
    id: number;
    name: string;
    photo: string;
}

function normalize(str: string): string {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

//----------------------------- Reviews: shared Google card builder
function buildGoogleCardHtml(r: any, allBrokers: Broker[] = [], isMainSite: boolean = true): string {
    const author = r.author_name || '—';
    const stars = r.rating ?? 5;
    const text = r.text || '';

    // Google API uses seconds for 'time'
    const dateObj = new Date((r.time || 0) * 1000);
    const dateStr = dateObj.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' });

    const starsHtml = Array.from({ length: 5 }, (_, i) =>
        `<span class="ReviewCard-star ${i < stars ? 'is-on' : ''}">★</span>`
    ).join('');

    // Avatar logic: User photo from Google or fallback reference.png
    const fallbackAvatar = '/wp-content/themes/delamerealitysrdcem/resources/images/reference.png';
    const finalAvatar = r.profile_photo_url || fallbackAvatar;

    // Matching logic for Broker (Flexible / Fuzzy with Scoring)
    let brokerHtml = '';
    if (isMainSite) {
        let matchedBroker: Broker | null = null;
        let bestScore = 0;
        if (text) {
            const normText = normalize(text);

            allBrokers.forEach(b => {
                const normName = normalize(b.name);
                const tokens = normName.split(' ').filter(t => t.length >= 3);
                let matchedTokensCount = 0;
                let score = 0;
                tokens.forEach(token => {
                    // Stemming: at least 3 chars, approx 75% of name
                    const stemLength = Math.max(3, Math.floor(token.length * 0.75));
                    const stem = token.substring(0, stemLength);

                    let foundTokenMatch = false;
                    let pos = normText.indexOf(stem);
                    while (pos !== -1) {
                        const isStartOfWord = pos === 0 || !/[a-z0-9]/.test(normText[pos - 1]);
                        
                        if (isStartOfWord) {
                            let endIdx = pos + stem.length;
                            while (endIdx < normText.length && /[a-z0-9]/.test(normText[endIdx])) {
                                endIdx++;
                            }
                            const wordInText = normText.substring(pos, endIdx);
                            
                            // Specific exclusions for common false positives in Czech
                            const stopWords = ['alespon', 'ale', 'pani', 'pan'];
                            if (stopWords.includes(wordInText)) {
                                pos = normText.indexOf(stem, pos + 1);
                                continue;
                            }

                            // Stricter matching: 
                            // 1. Exact match
                            if (wordInText === token) {
                                score += 20;
                                foundTokenMatch = true;
                            } 
                            // 2. Inflection match (short diff)
                            else {
                                const diff = Math.abs(wordInText.length - token.length);
                                // For short tokens (<= 4), we must be very strict
                                if (token.length <= 4) {
                                    if (diff <= 1) {
                                        score += 12;
                                        foundTokenMatch = true;
                                    }
                                } else {
                                    if (diff <= 2) {
                                        score += 10;
                                        foundTokenMatch = true;
                                    }
                                }
                            }
                        }
                        pos = normText.indexOf(stem, pos + 1);
                    }
                    if (foundTokenMatch) matchedTokensCount++;
                });

                // BONUS for matching both First Name and Last Name
                if (matchedTokensCount >= 2) {
                    score += 50;
                }

                if (score > bestScore) {
                    bestScore = score;
                    matchedBroker = b;
                }
            });
            // Minimum score to avoid random matches from common words
            if (bestScore < 10) matchedBroker = null;
        }

        brokerHtml = matchedBroker
            ? `
            <div class="ReviewCard-makler">
                ${matchedBroker.photo ? `<img class="ReviewCard-maklerAvatar" src="${matchedBroker.photo}" alt="${matchedBroker.name}" loading="lazy">` : ''}
                <span class="ReviewCard-maklerName">${matchedBroker.name}</span>
            </div>
        `
            : `
            <div class="ReviewCard-makler">
                <img class="ReviewCard-maklerAvatar" src="/wp-content/themes/delamerealitysrdcem/resources/images/logo-cerne.png" alt="Delame Reality Srdcem" loading="lazy">
            </div>
        `;
    } else {
        // Obrazovka makléře: zobrazit jeho avatar (pokud je v subwebu)
        // nebo prostě nic (fallback na logo)
        brokerHtml = `
            <div class="ReviewCard-makler">
                <img class="ReviewCard-maklerAvatar" src="/wp-content/themes/delamerealitysrdcem/resources/images/logo-cerne.png" alt="Delame Reality Srdcem" loading="lazy">
            </div>
        `;
    }

    return `
        <article class="ReviewCard">
            <div class="ReviewCard-header">
                <img class="ReviewCard-avatar" src="${finalAvatar}" onerror="this.src='${fallbackAvatar}'" alt="${author.replace(/"/g, '&quot;')}" width="48" height="48">
                <div class="ReviewCard-info">
                    <span class="ReviewCard-author">${author.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</span>
                    <span class="ReviewCard-date">${dateStr}</span>
                </div>
                <img class="ReviewCard-sourceIcon" src="/wp-content/themes/delamerealitysrdcem/resources/images/g.webp" alt="Google">
            </div>

            <div class="ReviewCard-body">
                <div class="ReviewCard-stars" aria-label="${stars} z 5">
                    ${starsHtml}
                </div>
                ${text ? `<div class="ReviewCard-text">${text.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>` : ''}
                ${brokerHtml}
            </div>
        </article>
    `;
}

function buildSidebarReviewHtml(r: any): string {
    const author = r.author_name || '—-';
    const stars = r.rating ?? 5;
    const text = r.text || '';
    const dateObj = new Date((r.time || 0) * 1000);
    const dateStr = dateObj.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
    
    const starsHtml = Array.from({ length: 5 }, (_, i) =>
        `<span class="ReviewCard-star ${i < stars ? 'is-on' : ''}">★</span>`
    ).join('');

    const fallbackAvatar = '/wp-content/themes/delamerealitysrdcem/resources/images/reference.png';
    const finalAvatar = r.profile_photo_url || fallbackAvatar;

    return `
        <article class="ReviewCard ReviewCard--sidebar">
            <div class="ReviewCard-header">
                <img class="ReviewCard-avatar" src="${finalAvatar}" onerror="this.src='${fallbackAvatar}'" alt="${author.replace(/"/g, '&quot;')}" width="40" height="40">
                <div class="ReviewCard-info">
                    <span class="ReviewCard-author">${author.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</span>
                    <span class="ReviewCard-date">${dateStr}</span>
                </div>
                <img class="ReviewCard-sourceIcon" src="/wp-content/themes/delamerealitysrdcem/resources/images/g.webp" alt="Google">
            </div>

            <div class="ReviewCard-body">
                <div class="ReviewCard-stars" aria-label="${stars} z 5">
                    ${starsHtml}
                </div>
                ${text ? `<div class="ReviewCard-text">${text.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>` : ''}
            </div>
        </article>
    `;
}

async function fetchGoogleReviewCards(apiKey: string, placeId: string, allBrokers: Broker[] = [], isMainSite: boolean = true, limit: number = 5): Promise<string[]> {
    await ensureGoogleMapsScript(apiKey);
    const details = await fetchPlaceDetails(placeId);
    const reviews = Array.isArray(details.reviews) ? details.reviews : [];

    return reviews
        .filter((r: any) => {
            const author = (r.author_name || '').toLowerCase();
            // Specific exclusion for Jakub Štastník (includes part of surname to allow other Jakubs)
            return !(author.includes('jakub') && author.includes('nastnik'));
        })
        .sort((a: any, b: any) => (b.time || 0) - (a.time || 0))
        .slice(0, limit)
        .map(r => buildGoogleCardHtml(r, allBrokers, isMainSite));
}

function interleaveHtml(existing: Element[], cards: string[], wrapTag?: string): string {
    let html = '';
    let gIdx = 0;
    existing.forEach(el => {
        html += el.outerHTML;
        if (gIdx < cards.length) {
            html += wrapTag ? `<${wrapTag}>${cards[gIdx++]}</${wrapTag}>` : cards[gIdx++];
        }
    });
    while (gIdx < cards.length) {
        html += wrapTag ? `<${wrapTag}>${cards[gIdx++]}</${wrapTag}>` : cards[gIdx++];
    }
    return html;
}

//----------------------------- Reviews Slider (HP) + Google injection
async function initReviewsSection() {
    const sliders = document.querySelectorAll<HTMLElement>('[data-reviews-slider]');
    if (!sliders.length) return;

    sliders.forEach(async (slider) => {
        const apiKey = slider.dataset.googleReviewsApiKey;
        const placeId = slider.dataset.googleReviewsPlaceId;
        const brokersRaw = slider.dataset.allBrokers;
        const isMainSite = slider.dataset.siteType === 'main';
        const wrapper = slider.querySelector<HTMLElement>('.swiper-wrapper');

        let allBrokers: Broker[] = [];
        try {
            if (brokersRaw) allBrokers = JSON.parse(brokersRaw);
        } catch (e) {
            console.warn('[GoogleReviews] Failed to parse brokers', e);
        }

        if (apiKey && placeId && wrapper) {
            try {
                const cards = await fetchGoogleReviewCards(apiKey, placeId, allBrokers, isMainSite);
                const existing = Array.from(wrapper.children);
                const wrappedCards = cards.map(c => `<div class="swiper-slide">${c}</div>`);
                wrapper.innerHTML = interleaveHtml(existing, wrappedCards);
            } catch (e) {
                console.error('[GoogleReviews] Slider error:', e);
            }
        }

        if (wrapper) applyReadMore(wrapper);

        const swiperInstance = new Swiper(slider, {
            modules: [Navigation],
            slidesPerView: 1,
            spaceBetween: 20,
            autoHeight: false,
            navigation: {
                prevEl: slider.querySelector<HTMLElement>('.HomeReviews-nav--prev'),
                nextEl: slider.querySelector<HTMLElement>('.HomeReviews-nav--next'),
            },
            breakpoints: {
                640: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
            },
        });

        if (wrapper) {
            wrapper.addEventListener('click', (e) => {
                if ((e.target as HTMLElement).classList.contains('ReviewCard-readMore')) {
                    setTimeout(() => swiperInstance.update(), 450);
                }
            });
        }
    });
}

//----------------------------- Read-more zkrácení textu recenzí
function applyReadMore(container: HTMLElement, wordLimit: number = 50) {
    const textEls = Array.from(container.querySelectorAll<HTMLElement>('.ReviewCard-text'));

    textEls.forEach(el => {
        // Zabráníme dvojí inicializaci na jedné kartě
        if (el.dataset.readMoreInit) return;
        el.dataset.readMoreInit = '1';

        const fullText = el.innerHTML.trim() ?? '';
        const plainText = el.textContent?.trim() ?? '';
        const words = plainText.split(/\s+/);

        if (words.length <= wordLimit) return; // krátký text

        // Vytvoříme strukturu pro animaci
        const wrapper = document.createElement('div');
        wrapper.className = 'ReviewCard-textValue is-static';
        wrapper.innerHTML = fullText; // Vždy vkládáme plný text - zkrácení/skrytí řeší CSS max-height a maska

        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'ReviewCard-readMore';
        toggle.textContent = 'Zobrazit více';

        el.innerHTML = '';
        el.appendChild(wrapper);
        el.appendChild(toggle);

        let expanded = false;
        toggle.addEventListener('click', () => {
            expanded = !expanded;

            if (expanded) {
                wrapper.classList.remove('is-static');
                wrapper.classList.add('is-expanded');
                toggle.textContent = 'Zobrazit méně';
            } else {
                wrapper.classList.remove('is-expanded');
                wrapper.classList.add('is-static');
                toggle.textContent = 'Zobrazit více';
            }
        });
    });
}

//----------------------------- Reviews Archive Grid + Google injection + client-side pagination
async function initReviewsArchive() {
    const root = document.querySelector<HTMLElement>('[data-reviews-archive]');
    if (!root) return;

    const apiKey = root.dataset.googleReviewsApiKey;
    const placeId = root.dataset.googleReviewsPlaceId;
    const brokersRaw = root.dataset.allBrokers;
    const isMainSite = root.dataset.siteType === 'main';
    const grid = root.querySelector<HTMLElement>('[data-reviews-archive-grid]');
    const pagerContainer = root.querySelector<HTMLElement>('[data-reviews-archive-pager]');
    const pageSize = parseInt(root.dataset.pageSize || '12', 10);

    if (!grid) return;

    // Inject Google reviews if API is configured
    if (apiKey && placeId) {
        let allBrokers: Broker[] = [];
        try {
            if (brokersRaw) allBrokers = JSON.parse(brokersRaw);
        } catch (e) {
            console.warn('[GoogleReviews] Failed to parse brokers', e);
        }
        try {
            const cards = await fetchGoogleReviewCards(apiKey, placeId, allBrokers, isMainSite);
            const existing = Array.from(grid.children);
            grid.innerHTML = interleaveHtml(existing, cards);
            applyReadMore(grid);
        } catch (e) {
            console.error('[GoogleReviews] Archive error:', e);
        }
    }

    // Client-side pagination
    const allCards = Array.from(grid.children) as HTMLElement[];
    if (allCards.length <= pageSize) return; // vše se vejde na jednu stránku

    let shown = pageSize;

    const applyVisibility = () => {
        allCards.forEach((card, i) => {
            card.style.display = i < shown ? '' : 'none';
        });
    };

    const renderPager = () => {
        if (!pagerContainer) return;
        const remaining = allCards.length - shown;
        if (remaining <= 0) {
            pagerContainer.innerHTML = '';
            return;
        }
        pagerContainer.innerHTML = `
            <div class="Pager u-frame" data-pager>
                <div class="Pager-spacing"></div>
                <div class="Pager-loadMore">
                    <div>
                        <a href="#" class="Button Button--primary" data-reviews-load-more>
                            <span>Načíst více</span>
                            <div class="ButtonCount Button--primaryIcon">
                                <span style="color: var(--colorDark)">${remaining}</span>
                                <div class="ButtonLoader" style="display:none"></div>
                            </div>
                        </a>
                    </div>
                </div>

            </div>
        `;
        pagerContainer.querySelector('[data-reviews-load-more]')?.addEventListener('click', (e) => {
            e.preventDefault();
            shown = Math.min(shown + pageSize, allCards.length);
            applyVisibility();
            renderPager();
        });
    };

    applyVisibility();
    renderPager();
}

//----------------------------- Sidebar Google injection
async function initSidebarReviews() {
    const container = document.querySelector<HTMLElement>('[data-sidebar-reviews]');
    if (!container) return;

    const apiKey = container.dataset.googleReviewsApiKey;
    const placeId = container.dataset.googleReviewsPlaceId;

    if (apiKey && placeId) {
        try {
            await ensureGoogleMapsScript(apiKey);
            const details = await fetchPlaceDetails(placeId);
            const reviews = Array.isArray(details.reviews) ? details.reviews : [];
            const sortedReviews = reviews
                .sort((a: any, b: any) => (b.time || 0) - (a.time || 0))
                .slice(0, 3);
            
            if (sortedReviews.length > 0) {
                container.innerHTML = sortedReviews.map(r => buildSidebarReviewHtml(r)).join('');
            } else {
                container.innerHTML = '<p class="u-textCenter">Zatím žádná hodnocení.</p>';
            }
        } catch (e) {
            console.error('[GoogleReviews] Sidebar error:', e);
            container.innerHTML = '';
        }
    }
}

let googleMapsScriptPromise: Promise<void> | null = null;
function ensureGoogleMapsScript(key: string): Promise<void> {
    if (googleMapsScriptPromise) return googleMapsScriptPromise;

    googleMapsScriptPromise = new Promise((resolve, reject) => {
        // If already has places, resolve immediately
        if ((window as any).google?.maps?.places) {
            console.log('[GoogleReviews] Google Maps Places already loaded');
            resolve();
            return;
        }

        const callbackName = '__initGoogleReviewsMaps';
        (window as any)[callbackName] = () => {
            console.log('[GoogleReviews] Google Maps script loaded via callback');
            resolve();
        };

        console.log('[GoogleReviews] Appending Google Maps script tag');
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places&language=cs&callback=${callbackName}`;
        script.async = true;
        script.defer = true;
        script.onerror = (err) => {
            console.error('[GoogleReviews] Script load error', err);
            reject(new Error('Failed to load Google Maps script'));
        };
        document.head.appendChild(script);

        // Fallback for cases where script might load but callback not fire or something
        setTimeout(() => {
            if ((window as any).google?.maps?.places) {
                console.log('[GoogleReviews] Fallback: Google Maps Places found after timeout');
                resolve();
            }
        }, 10000);
    });

    return googleMapsScriptPromise;
}

function fetchPlaceDetails(placeId: string): Promise<any> {
    return new Promise((resolve, reject) => {
        try {
            const dummy = document.createElement('div');
            const service = new (window as any).google.maps.places.PlacesService(dummy);

            service.getDetails(
                {
                    placeId,
                    fields: ["name", "rating", "user_ratings_total", "reviews", "url"]
                },
                (place: any, status: any) => {
                    if (status !== (window as any).google.maps.places.PlacesServiceStatus.OK) {
                        return reject(new Error(`Google Places error: ${status}`));
                    }
                    resolve(place);
                }
            );
        } catch (e) {
            reject(e);
        }
    });
}

function initComparisonSliders() {
    const sliders = document.querySelectorAll<HTMLElement>('.ComparisonSlider');
    sliders.forEach(slider => {
        const input = slider.querySelector<HTMLInputElement>('.ComparisonSlider-range');
        if (!input) return;

        input.addEventListener('input', () => {
            slider.style.setProperty('--rangeValue', `${input.value}%`);
        });
    });
}

//----------------------------- TopBar Google injection
async function initTopbarGoogle() {
    const el = document.querySelector<HTMLElement>('[data-topbar-google]');
    if (!el) return;

    const apiKey = el.dataset.googleApiKey;
    const placeId = el.dataset.googlePlaceId;

    if (apiKey && placeId) {
        try {
            await ensureGoogleMapsScript(apiKey);
            const details = await fetchPlaceDetails(placeId);
            const rating = details.rating || 0;
            const count = details.user_ratings_total || 0;
            const url = details.url || '#';

            if (rating > 0) {
                const ratingStr = rating.toLocaleString('cs-CZ', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                const ratingVal = el.querySelector('[data-rating-val]');
                const countText = el.querySelector('[data-count-text]');
                const stars = el.querySelectorAll<HTMLElement>('.TopBar-star');

                if (ratingVal) ratingVal.textContent = ratingStr.replace(',', '.');
                if (countText) countText.textContent = `na základě ${count}+ recenzí`;
                if (el.tagName === 'A' && url !== '#') (el as HTMLAnchorElement).href = url;

                const rounded = Math.round(rating);
                stars.forEach(s => {
                    const idx = parseInt(s.dataset.star || '0', 10);
                    s.classList.toggle('is-on', idx <= rounded);
                });

                el.classList.remove('u-hidden');
            }
        } catch (e) {
            console.error('[GoogleReviews] TopBar error:', e);
        }
    }
}

function runInit() {
    console.log('[GoogleReviews] runInit triggered, readyState:', document.readyState);
    initTopbarGoogle();
    initReviewsSection();
    initReviewsArchive();
    initSidebarReviews();
    initComparisonSliders();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInit);
} else {
    runInit();
}




